import React, { useEffect, useRef, useState } from 'react';
import { loadGisScript, startGoogleRedirectLogin } from '../utils/googleAuth';

const GoogleMark = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
    />
    <path
      fill="#FBBC05"
      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
    />
  </svg>
);

/**
 * Continue with Google.
 *
 * Primary path: GIS ID-token button (no client secret). This is what worked in
 * production before the OAuth code-exchange experiment.
 *
 * Fallback: full-page OAuth redirect when the GIS button is blocked / broken
 * (needs a valid GOOGLE_CLIENT_SECRET on AuthBackend).
 */
function GoogleSignIn({ onCredential, onError, label = 'Continue with Google' }) {
  const clientId = (process.env.REACT_APP_GOOGLE_CLIENT_ID || '').trim();
  const onErrorRef = useRef(onError);
  const onCredentialRef = useRef(onCredential);
  const buttonRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);

  onErrorRef.current = onError;
  onCredentialRef.current = onCredential;

  useEffect(() => {
    if (!clientId) {
      onErrorRef.current?.('Google Sign-In is not configured.');
      return undefined;
    }

    let cancelled = false;

    loadGisScript()
      .then(() => {
        if (cancelled || !buttonRef.current || !window.google?.accounts?.id) {
          throw new Error('Google Sign-In unavailable');
        }

        buttonRef.current.innerHTML = '';

        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (response) => {
            setBusy(false);
            if (response?.credential) {
              // ID token — AuthBackend verifies with client_id only (no secret).
              onCredentialRef.current?.(response.credential);
              return;
            }
            onErrorRef.current?.('Google Sign-In failed.');
          },
          auto_select: false,
          cancel_on_tap_outside: true,
          use_fedcm_for_button: true,
        });

        const width = Math.max(
          280,
          Math.floor(buttonRef.current.parentElement?.clientWidth || 320)
        );

        window.google.accounts.id.renderButton(buttonRef.current, {
          type: 'standard',
          theme: 'filled_black',
          size: 'large',
          text: 'continue_with',
          shape: 'rectangular',
          logo_alignment: 'left',
          width,
        });

        if (!cancelled) setReady(true);
      })
      .catch(() => {
        if (!cancelled) {
          onErrorRef.current?.(
            'Could not load Google Sign-In. Check that accounts.google.com is allowed, then refresh.'
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [clientId]);

  if (!clientId) {
    return null;
  }

  const triggerGisClick = () => {
    const root = buttonRef.current;
    if (!root) return false;
    const target =
      root.querySelector('div[role="button"]') ||
      root.querySelector('iframe') ||
      root.firstElementChild;
    if (!target) return false;
    target.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
    if (typeof target.click === 'function') target.click();
    return true;
  };

  const handleFallbackRedirect = () => {
    setBusy(true);
    try {
      startGoogleRedirectLogin(clientId);
    } catch (err) {
      setBusy(false);
      onErrorRef.current?.(
        err?.message || 'Could not redirect to Google. Please try again.'
      );
    }
  };

  const handleCustomClick = () => {
    if (!ready || busy) return;
    setBusy(true);
    // Prefer forwarding the click into the GIS control (keeps ID-token flow).
    if (!triggerGisClick()) {
      handleFallbackRedirect();
    } else {
      // If GIS never responds (blocked iframe), offer redirect after a beat.
      window.setTimeout(() => {
        setBusy(false);
      }, 4000);
    }
  };

  return (
    <div className="google-continue-wrap">
      <div className="google-continue">
        <button
          type="button"
          className="google-continue__face"
          onClick={handleCustomClick}
          disabled={!ready || busy}
          aria-busy={busy}
        >
          <span className="google-continue__icon">
            <GoogleMark />
          </span>
          <span className="google-continue__label">
            {busy ? 'Opening Google…' : ready ? label : 'Loading Google…'}
          </span>
        </button>
        {/* GIS ID-token button: receives the real click when overlay works */}
        <div ref={buttonRef} className="google-continue__gis" aria-hidden="true" />
      </div>

      <button
        type="button"
        className="google-continue__fallback"
        onClick={handleFallbackRedirect}
        disabled={busy}
      >
        Having trouble? Continue on Google
      </button>
    </div>
  );
}

export default GoogleSignIn;
